/**
 * BodyBlurredImage — iter172o zentraler Frontend-Renderer für per-Region-Blur.
 *
 * Zeigt für non-AVS-User das serverseitig gerenderte Bild aus
 * `GET /api/media/body-blurred/{listing_id}?url=<worn_url>`. Der Backend-
 * Endpoint nutzt EXAKT dieselbe Pipeline wie das Admin-Testtool
 * (`BodyRegionsService.apply_region_blur`) — alle Config-Änderungen im Admin
 * wirken automatisch (Cache wird beim Config-Save invalidiert).
 *
 * Für AVS-verifizierte User + Owner-Vorschau wird das Original-Bild
 * direkt gerendert.
 *
 * Nutzung:
 *   <BodyBlurredImage
 *     listingId={listing.listing_id}
 *     rawUrl={worn_url}
 *     hasRegions={true|false}
 *     isViewerAvsVerified={user?.avs_verified}
 *     alt="Trage-Foto"
 *     className="w-full h-full object-cover"
 *   />
 */
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import AVSBlurBadge from "./AVSBlurBadge";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function buildBlurredUrl(listingId, rawUrl, cacheBust) {
  const bust = cacheBust ? `&v=${encodeURIComponent(cacheBust)}` : "";
  const u = `${API_BASE}/api/media/body-blurred/${encodeURIComponent(listingId)}?url=${encodeURIComponent(rawUrl)}${bust}`;
  return u;
}

export default function BodyBlurredImage({
  listingId,
  rawUrl,
  hasRegions = false,
  isViewerAvsVerified = false,
  alt = "",
  className = "",
  loading = "lazy",
  onClick = undefined,
  testId = "body-blurred-image",
  cacheBust = "",
}) {
  const { t } = useTranslation();
  const [errored, setErrored] = useState(false);

  useEffect(() => { setErrored(false); }, [listingId, rawUrl]);

  // Kein Blur nötig
  if (isViewerAvsVerified || !hasRegions) {
    return (
      <img
        data-testid={testId}
        src={rawUrl}
        alt={alt}
        className={className}
        loading={loading}
        onClick={onClick}
        draggable={false}
      />
    );
  }

  // Non-AVS + Regionen vorhanden → serverseitig geblurrtes PNG + 18+ Badge overlay
  const src = errored ? "" : buildBlurredUrl(listingId, rawUrl, cacheBust);
  return (
    <>
      <img
        data-testid={testId}
        data-blurred="true"
        src={src}
        alt={alt}
        className={className}
        loading={loading}
        onClick={onClick}
        onError={() => setErrored(true)}
        draggable={false}
      />
      {/* iter173b · Einheitlicher 18+-Marker über AVS-Blur */}
      {!errored && <AVSBlurBadge size="md" testId={`${testId}-avs-badge`} />}
      {errored && (
        // Wenn der Blur-Endpoint scheitert (Netzwerkfehler, 5xx): kein
        // Original zeigen — komplett schwarze Fläche als sicherer Fallback.
        <div
          data-testid={`${testId}-fallback`}
          className="absolute inset-0 bg-black"
          aria-label={t("media.image_unavailable")}
        />
      )}
    </>
  );
}

BodyBlurredImage.propTypes = {
  listingId: PropTypes.string.isRequired,
  rawUrl: PropTypes.string.isRequired,
  hasRegions: PropTypes.bool,
  isViewerAvsVerified: PropTypes.bool,
  alt: PropTypes.string,
  className: PropTypes.string,
  loading: PropTypes.string,
  onClick: PropTypes.func,
  testId: PropTypes.string,
};
